import { useCallback, useRef, useSyncExternalStore } from 'react'

/**
 * Meelezen met iets buiten React, met een momentopname die stabiel blijft.
 *
 * `useSyncExternalStore` vergelijkt de momentopname met `Object.is` en hertekent zolang hij
 * verschilt. Een lezer die per aanroep een **nieuw** object of een nieuwe lijst maakt levert dus
 * bij elke render een andere waarde, en React loopt in een lus tot hij afbreekt met "Maximum
 * update depth exceeded". Dat is bij het bouwen van de presence-balk gebeurd, en het is een
 * valkuil die je maar één keer hoeft te ontdekken: de melding wijst naar React en de oorzaak
 * staat in de lezer.
 *
 * **Deze hook is de enige plek waar dat wordt opgelost.** `useStand` had er zijn eigen antwoord
 * op, met dezelfde vergelijking op inhoud; die staat nu hier, zodat de drie afnemers (de stand,
 * de draadjes en de presence) er niet drie varianten van houden.
 *
 * **Vergelijken op inhoud en niet op een vuilvlag**, en dat is de afweging die telt. Een vlag die
 * bij elke gemelde wijziging aangaat is goedkoper, en hij hertekent ook als de wijziging voor
 * deze lezer niets betekent. Dat is precies het gewone geval: een Yjs-update komt bij elke
 * toetsaanslag langs, de pod stempelt er een tweede transactie achteraan, en `observeDeep` op een
 * dradenmap vuurt ook voor een citaat dat niemand leest. Een vergelijking over de uitkomst kost
 * één serialisatie per melding en houdt de hertekening bij wat er werkelijk is veranderd.
 *
 * Wie een lezer meegeeft die niet serialiseerbaar is (een `Map`, een klasse-instantie) krijgt
 * hier geen fout, maar de faalwijze is de omgekeerde van wat hier eerst stond: geen vergelijking
 * die altijd verschilt, maar een die nooit verschilt. `JSON.stringify(new Map([['a', 1]]))` is
 * `{}`, dus de sleutel is constant, wordt na de eerste lezing nooit meer bijgewerkt en de lezer
 * levert voorgoed de eerste waarde. Stille veroudering dus, en geen renderlus, en dat is de
 * lastigere van de twee om te vinden. Geef platte data terug; alle afnemers doen dat.
 */
export function useSnapshot<T>(
  subscribe: (hook: () => void) => () => void,
  read: () => T,
): T {
  const previous = useRef<{ sleutel: string; waarde: T } | undefined>(undefined)

  const snapshot = useCallback((): T => {
    const value = read()
    /**
     * `?? 'undefined'` en dat is geen nettigheid.
     *
     * `JSON.stringify(undefined)` levert `undefined` en niet een string. Zonder deze terugval is
     * de sleutel bij de eerste lezing van een leeg veld gelijk aan `vorige.current?.sleutel`,
     * namelijk beide `undefined`, en dan wordt de cache nooit gevuld en valt de volgende regel om.
     * Een leeg veld is het gewone geval: elke cel in een herhaalgroep begint zo.
     */
    const key = JSON.stringify(value) ?? 'undefined'
    if (previous.current?.sleutel !== key) previous.current = { sleutel: key, waarde: value }
    return previous.current.waarde
  }, [read])

  /**
   * **Een melding uit Yjs bereikt React nooit synchroon** (gevonden bij het invulscherm van 29
   * oktober 2026, stil dataverlies).
   *
   * De hooks hier hangen aan `observe` en `observeDeep` van Yjs, en die roept Yjs aan midden in het
   * afronden van een transactie (`cleanupTransactions`), vóór het `update`-event waarmee de provider
   * de wijziging naar de pod stuurt. Riep de melding React synchroon aan, dan gooide React bij snel
   * typen in rich text af en toe "Maximum update depth exceeded" (een keten van geneste synchrone
   * updates over de editor heen), en die fout brak de afronding van de transactie af: de wijziging
   * stond in het document in de browser, het scherm zei dat alles was opgeslagen, en de pod kreeg
   * haar nooit. Een keuzelijst die daarna werd gekozen, ging zo bij indienen verloren.
   *
   * Daarom gaat de melding naar een microtask, en meerdere meldingen in dezelfde taak worden er
   * één. React leest daarna de stand zoals hij dan is (`snapshot` hierboven), dus er gaat niets
   * verloren; het scherm loopt hooguit één microtask achter op het document.
   */
  const deferred = useCallback(
    (hook: () => void) => {
      let live = true
      let pending = false
      const notify = (): void => {
        if (pending) return
        pending = true
        queueMicrotask(() => {
          pending = false
          if (live) hook()
        })
      }
      const unsubscribe = subscribe(notify)
      return () => {
        live = false
        unsubscribe()
      }
    },
    [subscribe],
  )

  return useSyncExternalStore(deferred, snapshot, snapshot)
}
